'use client';

import { useState, useMemo } from 'react';
import { WishlistItem as WishlistItemType, Tag } from '@home-service/contracts';
import WishlistItem from './WishlistItem';
import MobileMenu from '@/components/shared/MobileMenu';
import ThemeToggle from '@/components/shared/ThemeToggle';
import { PRICE_GROUPS } from '@/lib/config';
import styles from '@/app/Home.module.css';

interface WishlistContentProps {
    initialItems: WishlistItemType[];
    initialTags: Tag[];
    version: string;
}

function groupItemsByPrice(items: WishlistItemType[]) {
    // Sort by price ascending
    const sorted = [...items].sort((a, b) => (a.price || 0) - (b.price || 0));

    // Group by price ranges
    const groups = PRICE_GROUPS.map(group => ({
        ...group,
        items: sorted.filter(item => {
            const price = item.price || 0;
            return price >= group.min && price < group.max;
        }),
    }));

    // Return only non-empty groups
    return groups.filter(group => group.items.length > 0);
}

export default function WishlistContent({ initialItems, initialTags, version }: WishlistContentProps) {
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Filter items based on selected tags
    const filteredItems = useMemo(() => {
        if (selectedTags.length === 0) return initialItems;
        return initialItems.filter(item => {
            if (!item.tags || item.tags.length === 0) return false;
            return item.tags.some(tag => selectedTags.includes(tag.name));
        });
    }, [initialItems, selectedTags]);

    const groupedItems = useMemo(() => groupItemsByPrice(filteredItems), [filteredItems]);

    const toggleTag = (tagName: string) => {
        setSelectedTags(prev =>
            prev.includes(tagName)
                ? prev.filter(t => t !== tagName)
                : [...prev, tagName]
        );
    };

    // Calculate global index for uniqueness
    let globalIndex = 0;

    return (
        <div className={styles.layout}>
            {/* SIDEBAR via MobileMenu component */}
            <MobileMenu>
                <div className={styles.sidebarHeader}>
                    <div>
                        <h1 className={styles.sidebarTitle}>
                            <span style={{ color: 'var(--primary)' }}>//</span> WISHLIST
                        </h1>
                        <p className={styles.sidebarVersion}>
                            v{version}
                        </p>
                    </div>
                    <ThemeToggle />
                </div>

                {/* TAGS FILTER */}
                <nav className={styles.navSection}>
                    <div className={styles.navTitle} style={{ marginBottom: '8px' }}>
                        <span style={{ color: 'var(--comment)' }}>──</span> tags <span style={{ color: 'var(--comment)' }}>──────</span>
                    </div>
                    {initialTags.length === 0 ? (
                        <div style={{ padding: '0 12px', color: 'var(--fg-muted)', fontSize: '13px' }}>
                            (no tags)
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '0 12px', marginBottom: '24px' }}>
                            {initialTags.map(tag => {
                                const isSelected = selectedTags.includes(tag.name);
                                return (
                                    <button
                                        key={tag.id}
                                        onClick={() => toggleTag(tag.name)}
                                        style={{
                                            background: isSelected ? `var(--${tag.color})` : 'transparent',
                                            color: isSelected ? 'var(--bg-card)' : `var(--${tag.color})`,
                                            border: `1px solid var(--${tag.color})`,
                                            padding: '2px 6px',
                                            fontSize: '11px',
                                            fontFamily: 'var(--font-mono)',
                                            cursor: 'pointer',
                                            textTransform: 'uppercase',
                                            opacity: selectedTags.length === 0 || isSelected ? 1 : 0.5,
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        {tag.name}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </nav>

                <nav className={styles.navSection}>
                    <div className={styles.navTitle}>
                        <span style={{ color: 'var(--comment)' }}>──</span> items <span style={{ color: 'var(--comment)' }}>──────</span>
                    </div>
                    {groupedItems.map((group) => (
                        <div key={group.id} className={styles.sidebarGroup}>
                            <div className={styles.sidebarGroupLabel}>
                                <span style={{ color: 'var(--green)' }}>▸</span> {group.label}
                            </div>
                            {group.items.map((item, idx) => {
                                const isLast = idx === group.items.length - 1;
                                return (
                                    <a
                                        key={item.id}
                                        href={`#item-${item.id}`}
                                        className={styles.navItem}
                                    >
                                        <span style={{ color: 'var(--comment)', marginRight: '8px' }}>
                                            {isLast ? '└─' : '├─'}
                                        </span>
                                        {item.title}
                                    </a>
                                );
                            })}
                        </div>
                    ))}
                </nav>
            </MobileMenu>

            {/* MAIN CONTENT */}
            <main className={styles.main}>
                <header className={styles.pageHeader}>
                    <h2 className={styles.pageTitle}>
                        Global Wishlist
                    </h2>
                    <p className={styles.pageSubtitle}>
                        <span className={styles.code}>root/collections/wishlist.json</span>
                    </p>
                </header>

                <div>
                    {filteredItems.length === 0 ? (
                        <div className={styles.emptyCard}>
                            <div className={styles.emptyEmoji}>📦</div>
                            <p style={{ color: 'var(--fg-muted)', marginBottom: '16px' }}>
                                {initialItems.length > 0 ? 'Нет товаров с выбранными тегами' : 'Список пуст'}
                            </p>
                        </div>
                    ) : (
                        groupedItems.map(group => {
                            return (
                                <section key={group.id} className={styles.priceGroup}>
                                    <div className={styles.priceGroupHeader}>
                                        <span className={styles.priceGroupLabel}>
                                            {group.label}
                                        </span>
                                        <span className={styles.priceGroupCount}>
                                            {group.items.length}
                                        </span>
                                    </div>

                                    {/* Grid Container */}
                                    <div className={styles.cardGrid}>
                                        {group.items.map(item => {
                                            const itemGlobalIndex = globalIndex++;
                                            return (
                                                <WishlistItem
                                                    key={item.id}
                                                    item={item}
                                                    index={itemGlobalIndex}
                                                />
                                            );
                                        })}
                                    </div>
                                </section>
                            );
                        })
                    )}
                </div>
            </main>
        </div>
    );
}
